import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Drawer,
  Input,
  InputNumber,
  Space,
  Switch,
  Tag,
  Typography,
} from "antd";
import { ArrowRight, Calendar, Check, Sliders, X } from "react-feather";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const { Text, Title } = Typography;

const DOW = [
  { key: 1, label: "Mon" },
  { key: 2, label: "Tue" },
  { key: 3, label: "Wed" },
  { key: 4, label: "Thu" },
  { key: 5, label: "Fri" },
  { key: 6, label: "Sat" },
  { key: 0, label: "Sun" },
];

const currencySymbol = (code) => {
  if (code === "INR") return "₹";
  if (code === "GBP") return "£";
  if (code === "USD") return "$";
  if (code === "EUR") return "€";
  return `${code || ""} `;
};

const FieldRow = ({
  checked,
  onCheckedChange,
  title,
  help,
  children,
}) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "minmax(220px, 1fr) minmax(180px, 250px)",
      gap: 18,
      alignItems: "center",
      padding: "12px 13px",
      marginBottom: 8,
      border: checked ? "1px solid #cfe0ff" : "1px solid #e8edf3",
      borderRadius: 12,
      background: checked ? "linear-gradient(135deg,#f8fbff,#ffffff)" : "#fff",
      boxShadow: checked ? "0 5px 16px rgba(21,94,239,.05)" : "none",
      transition: "border-color .15s ease, box-shadow .15s ease, background .15s ease",
    }}
  >
    <div>
      <Checkbox checked={checked} onChange={(e) => onCheckedChange(e.target.checked)}>
        <span style={{ fontWeight: 900, color: "#172033", fontSize: 13 }}>{title}</span>
      </Checkbox>
      {help ? (
        <div style={{ marginLeft: 24, marginTop: 4, color: "#7b8497", fontSize: 11, lineHeight: 1.45 }}>
          {help}
        </div>
      ) : null}
    </div>
    <div style={{ opacity: checked ? 1 : 0.45, pointerEvents: checked ? "auto" : "none" }}>
      {children}
    </div>
  </div>
);

const ToggleValue = ({ value, onChange, onLabel = "On", offLabel = "Off" }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 9,
    }}
  >
    <Text style={{ fontSize: 12, fontWeight: 700, color: value ? "#1677ff" : "#7b8497" }}>
      {value ? onLabel : offLabel}
    </Text>
    <Switch checked={value} onChange={onChange} />
  </div>
);

export default function BeBulkUpdateDrawer({
  open,
  onClose,
  onApply,
  roomType,
  month,
  businessDate,
  currency = "INR",
  rateOptions = [],
}) {
  const monthStart = useMemo(() => moment(month).startOf("month"), [month]);
  const monthEnd = useMemo(() => moment(month).endOf("month"), [month]);
  const firstEditableDate = useMemo(
    () => moment.max(monthStart.clone(), moment(businessDate).startOf("day")),
    [monthStart, businessDate]
  );

  const [dateRange, setDateRange] = useState([firstEditableDate, monthEnd]);
  const [applying, setApplying] = useState(false);
  const [selectedDow, setSelectedDow] = useState(DOW.map((d) => d.key));

  const [allotmentEnabled, setAllotmentEnabled] = useState(false);
  const [allotment, setAllotment] = useState(roomType?.total_rooms || 0);

  const [minStayEnabled, setMinStayEnabled] = useState(false);
  const [minStay, setMinStay] = useState(1);

  const [stopSellEnabled, setStopSellEnabled] = useState(false);
  const [stopSell, setStopSell] = useState(false);

  const [ctaEnabled, setCtaEnabled] = useState(false);
  const [cta, setCta] = useState(false);

  const [ctdEnabled, setCtdEnabled] = useState(false);
  const [ctd, setCtd] = useState(false);

  // Rate-option-specific bulk overrides. A plan must be explicitly selected,
  // and only entered fields are merged into each target date.
  const [rateEdits, setRateEdits] = useState({});
  const [legacyRateEnabled, setLegacyRateEnabled] = useState(false);
  const [legacyOverrideRate, setLegacyOverrideRate] = useState(null);
  const [currencyCode, setCurrencyCode] = useState(currency || "INR");

  useEffect(() => {
    if (!open) return;
    const start = moment.max(moment(month).startOf("month"), moment(businessDate).startOf("day"));
    const end = moment(month).endOf("month");
    setDateRange([start, end]);
    setSelectedDow(DOW.map((d) => d.key));
    setAllotmentEnabled(false);
    setAllotment(roomType?.total_rooms || 0);
    setMinStayEnabled(false);
    setMinStay(1);
    setStopSellEnabled(false);
    setStopSell(false);
    setCtaEnabled(false);
    setCta(false);
    setCtdEnabled(false);
    setCtd(false);
    setRateEdits({});
    setLegacyRateEnabled(false);
    setLegacyOverrideRate(null);
    setCurrencyCode(currency || "INR");
    setApplying(false);
  }, [open, month, businessDate, roomType, currency, rateOptions]);

  const affectedDates = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1] || !selectedDow.length) return [];
    const allowed = new Set(selectedDow);
    const start = moment(dateRange[0]).startOf("day");
    const end = moment(dateRange[1]).startOf("day");
    const floor = moment(businessDate).startOf("day");
    const dates = [];
    const cursor = start.clone();

    while (cursor.isSameOrBefore(end, "day")) {
      if (
        cursor.isSameOrAfter(floor, "day") &&
        allowed.has(cursor.day())
      ) {
        dates.push(cursor.format("YYYY-MM-DD"));
      }
      cursor.add(1, "day");
    }
    return dates;
  }, [dateRange, selectedDow, businessDate]);

  const patchRateEdit = (rateId, patch) => {
    const key = String(Number(rateId));
    setRateEdits((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || { enabled: false, rate_id: Number(rateId), guest_wise_rates: {} }),
        ...patch,
        rate_id: Number(rateId),
      },
    }));
  };

  const activeRateOverrides = useMemo(() => {
    return (rateOptions || []).flatMap((rate) => {
      const row = rateEdits[String(Number(rate.rate_id))];
      if (!row?.enabled) return [];

      if (rate.is_guest_wise_rate) {
        const guestWise = {};
        Object.entries(row.guest_wise_rates || {}).forEach(([guestCount, value]) => {
          const amount = Number(value);
          if (value !== null && value !== "" && value !== undefined && Number.isFinite(amount) && amount >= 0) {
            guestWise[String(guestCount)] = amount;
          }
        });
        if (!Object.keys(guestWise).length) return [];
        return [{ rate_id: Number(rate.rate_id), guest_wise_rates: guestWise, currency_code: currencyCode || currency || "INR" }];
      }

      const occ1 = row.occ1_rate === null || row.occ1_rate === "" || row.occ1_rate === undefined ? null : Number(row.occ1_rate);
      const occ2 = row.occ2_rate === null || row.occ2_rate === "" || row.occ2_rate === undefined ? null : Number(row.occ2_rate);
      if (!(Number.isFinite(occ1) && occ1 >= 0) && !(Number.isFinite(occ2) && occ2 >= 0)) return [];
      return [{
        rate_id: Number(rate.rate_id),
        occ1_rate: Number.isFinite(occ1) && occ1 >= 0 ? occ1 : null,
        occ2_rate: Number.isFinite(occ2) && occ2 >= 0 ? occ2 : null,
        guest_wise_rates: {},
        currency_code: currencyCode || currency || "INR",
      }];
    });
  }, [rateOptions, rateEdits, currencyCode, currency]);

  const hasLegacyRateChange =
    !(rateOptions || []).length &&
    legacyRateEnabled &&
    legacyOverrideRate !== null &&
    legacyOverrideRate !== "" &&
    Number.isFinite(Number(legacyOverrideRate)) &&
    Number(legacyOverrideRate) >= 0;

  const hasAnyChange =
    allotmentEnabled ||
    minStayEnabled ||
    stopSellEnabled ||
    ctaEnabled ||
    ctdEnabled ||
    activeRateOverrides.length > 0 ||
    hasLegacyRateChange;

  const exceedsPhysicalInventory =
    allotmentEnabled &&
    Number(roomType?.total_rooms || 0) > 0 &&
    Number(allotment || 0) > Number(roomType?.total_rooms || 0);

  const setDowPreset = (preset) => {
    if (preset === "all") setSelectedDow(DOW.map((d) => d.key));
    if (preset === "weekdays") setSelectedDow([1, 2, 3, 4, 5]);
    if (preset === "weekends") setSelectedDow([6, 0]);
  };

  const toggleDow = (day) => {
    setSelectedDow((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const setReactDate = (index, rawDate) => {
    if (!rawDate) return;
    let next = moment(rawDate).startOf("day");
    if (!next.isValid()) return;
    const floor = moment(businessDate).startOf("day");
    if (next.isBefore(floor, "day")) next = floor.clone();

    setDateRange((current) => {
      const fallbackStart = moment.max(monthStart.clone(), floor.clone());
      const fallbackEnd = fallbackStart.clone().endOf("month");
      const range = current?.length === 2 ? [...current] : [fallbackStart, fallbackEnd];
      range[index] = next;
      if (index === 0 && next.isAfter(range[1], "day")) range[1] = next.clone();
      if (index === 1 && next.isBefore(range[0], "day")) range[0] = next.clone();
      return range;
    });
  };

  const setRangePreset = (preset) => {
    const floor = moment(businessDate).startOf("day");
    const start = dateRange?.[0] && moment(dateRange[0]).isSameOrAfter(floor, "day")
      ? moment(dateRange[0]).startOf("day")
      : moment.max(monthStart.clone(), floor.clone());

    const days = preset === "7" ? 6 : preset === "14" ? 13 : 29;
    setDateRange([start, start.clone().add(days, "days")]);
  };

  const apply = async () => {
    if (!affectedDates.length || !hasAnyChange) return;

    const patch = {};
    if (allotmentEnabled) patch.allotment = Math.max(0, Number(allotment || 0));
    if (minStayEnabled) patch.minStay = Math.max(1, Number(minStay || 1));
    if (stopSellEnabled) patch.stopSell = !!stopSell;
    if (ctaEnabled) patch.cta = !!cta;
    if (ctdEnabled) patch.ctd = !!ctd;
    if (hasLegacyRateChange) {
      patch.overrideRate = Number(legacyOverrideRate);
      patch.currencyCode = String(currencyCode || currency || "INR").toUpperCase();
    }
    try {
      setApplying(true);
      await onApply?.({
      dateKeys: affectedDates,
      patch,
      rateOverrides: activeRateOverrides,
      meta: {
        affectedCount: affectedDates.length,
        roomTypeId: roomType?.id,
        roomTypeName: roomType?.name,
      },
      });
      onClose?.();
    } finally {
      setApplying(false);
    }
  };

  const fullMonthIsPast = monthEnd.isBefore(moment(businessDate).startOf("day"), "day");

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="min(780px, 100vw)"
      destroyOnClose={false}
      zIndex={100000}
      getContainer={() => document.body}
      closeIcon={<X size={19} strokeWidth={2} />}
      className="be-bulk-premium-drawer"
      title={
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <Sliders size={16} color="#1677ff" />
            <span style={{ fontWeight: 900 }}>Bulk Update</span>
          </div>
          <div style={{ marginTop: 3, fontSize: 12, color: "#7b8497", fontWeight: 500 }}>
            Stage changes across multiple dates, then review them in the calendar before saving.
          </div>
        </div>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div style={{ color: "#7b8497", fontSize: 12 }}>
            <b style={{ color: "#172033" }}>{affectedDates.length}</b> date{affectedDates.length === 1 ? "" : "s"} will change
          </div>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              icon={<Check size={15} />}
              onClick={apply}
              loading={applying}
              disabled={!affectedDates.length || !hasAnyChange || fullMonthIsPast || applying}
            >
              Apply to Calendar
            </Button>
          </Space>
        </div>
      }
      bodyStyle={{ padding: "18px 22px 28px", background: "#f7f9fc" }}
      headerStyle={{ padding: "18px 22px", borderBottom: "1px solid #e7ecf3", background: "#fff" }}
      footerStyle={{ padding: "13px 22px", borderTop: "1px solid #e7ecf3", background: "rgba(255,255,255,.98)", backdropFilter: "blur(12px)" }}
    >
      <div className="be-bulk-scope-card">
        <div style={{ fontSize: 11, color: "#7b8497", textTransform: "uppercase", letterSpacing: ".07em", fontWeight: 800 }}>
          Editing scope
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 6, alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 900, color: "#172033", fontSize: 15 }}>{roomType?.name || "Room Type"}</div>
            <div style={{ color: "#7b8497", fontSize: 12, marginTop: 2 }}>
              {roomType?.total_rooms || 0} physical rooms · viewing {moment(month).format("MMMM YYYY")}
            </div>
          </div>
          <Tag color="blue">One room type</Tag>
        </div>
      </div>

      {fullMonthIsPast ? (
        <Alert
          type="info"
          showIcon
          message="This month is in the past"
          description="Bulk editing follows the same rule as the calendar: past dates are read-only."
          style={{ marginBottom: 18, borderRadius: 10 }}
        />
      ) : null}

      <div className="be-bulk-section-card">
        <div className="be-bulk-section-head">
          <div className="be-bulk-section-icon"><Calendar size={16} /></div>
          <div>
            <Title level={5} style={{ margin: 0, fontSize: 14 }}>Dates to update</Title>
            <Text type="secondary" style={{ fontSize: 12 }}>Choose the exact stay dates this change should affect.</Text>
          </div>
          <div className="be-bulk-quick-ranges">
            <button type="button" className="btn btn-sm btn-light border" onClick={() => setRangePreset("7")}>Next 7 days</button>
            <button type="button" className="btn btn-sm btn-light border" onClick={() => setRangePreset("14")}>Next 14 days</button>
            <button type="button" className="btn btn-sm btn-light border" onClick={() => setRangePreset("30")}>Next 30 days</button>
          </div>
        </div>

        <div className="be-bulk-react-range mt-3">
          <div className="be-bulk-react-date-field">
            <label className="be-bulk-date-label">Start date</label>
            <div className="be-bulk-react-date-wrap">
              <Calendar size={15} className="be-bulk-react-date-icon" />
              <DatePicker
                selected={dateRange?.[0]?.toDate?.() || null}
                onChange={(date) => setReactDate(0, date)}
                selectsStart
                startDate={dateRange?.[0]?.toDate?.() || null}
                endDate={dateRange?.[1]?.toDate?.() || null}
                minDate={firstEditableDate.toDate()}
                dateFormat="dd MMM yyyy"
                className="be-bulk-react-date-input"
                calendarClassName="be-bulk-react-calendar"
                popperClassName="be-bulk-react-popper"
                showPopperArrow={false}
                placeholderText="Select start date"
              />
            </div>
            <div className="be-bulk-date-help">Past dates are disabled. You can select any future date from {moment(businessDate).format("DD MMM YYYY")} onward.</div>
          </div>

          <div className="be-bulk-range-arrow" aria-hidden="true"><ArrowRight size={17} /></div>

          <div className="be-bulk-react-date-field">
            <label className="be-bulk-date-label">End date</label>
            <div className="be-bulk-react-date-wrap">
              <Calendar size={15} className="be-bulk-react-date-icon" />
              <DatePicker
                selected={dateRange?.[1]?.toDate?.() || null}
                onChange={(date) => setReactDate(1, date)}
                selectsEnd
                startDate={dateRange?.[0]?.toDate?.() || null}
                endDate={dateRange?.[1]?.toDate?.() || null}
                minDate={(dateRange?.[0] || firstEditableDate).toDate()}
                dateFormat="dd MMM yyyy"
                className="be-bulk-react-date-input"
                calendarClassName="be-bulk-react-calendar"
                popperClassName="be-bulk-react-popper"
                showPopperArrow={false}
                placeholderText="Select end date"
              />
            </div>
            <div className="be-bulk-date-help">Future months are enabled. Cross-month changes are staged and saved with the page.</div>
          </div>
        </div>

        <div className="be-bulk-days-wrap">
          <div className="be-bulk-days-head">
            <Text style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>Apply on</Text>
            <div className="btn-group btn-group-sm" role="group" aria-label="Day presets">
              <button type="button" className={`btn ${selectedDow.length === 7 ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setDowPreset("all")}>All days</button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setDowPreset("weekdays")}>Weekdays</button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setDowPreset("weekends")}>Weekends</button>
            </div>
          </div>
          <div className="be-bulk-dow-grid">
            {DOW.map((d) => {
              const active = selectedDow.includes(d.key);
              return (
                <button
                  type="button"
                  key={d.key}
                  className={`be-bulk-dow ${active ? "is-active" : ""}`}
                  onClick={() => toggleDow(d.key)}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="be-bulk-section-card" style={{ marginTop: 14 }}>

      <div className="be-bulk-section-head" style={{ marginBottom: 12 }}>
        <div className="be-bulk-section-icon"><Sliders size={16} /></div>
        <div>
          <Title level={5} style={{ margin: 0, fontSize: 14 }}>Choose what to change</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Only enabled controls are staged. Everything else remains untouched.</Text>
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        <FieldRow
          checked={allotmentEnabled}
          onCheckedChange={setAllotmentEnabled}
          title="Available rooms"
          help="Number of rooms the Booking Engine can sell."
        >
          <InputNumber
            min={0}
            max={999}
            value={allotment}
            onChange={setAllotment}
            style={{ width: "100%" }}
            addonAfter="rooms"
          />
        </FieldRow>

        <FieldRow
          checked={minStayEnabled}
          onCheckedChange={setMinStayEnabled}
          title="Minimum stay"
          help="Minimum number of nights required for arrival on matching dates."
        >
          <InputNumber
            min={1}
            max={30}
            value={minStay}
            onChange={setMinStay}
            style={{ width: "100%" }}
            addonAfter="nights"
          />
        </FieldRow>

        <FieldRow
          checked={stopSellEnabled}
          onCheckedChange={setStopSellEnabled}
          title="Stop sell"
          help="Close or reopen inventory for sale."
        >
          <ToggleValue value={stopSell} onChange={setStopSell} onLabel="Closed" offLabel="Selling" />
        </FieldRow>

        <FieldRow
          checked={ctaEnabled}
          onCheckedChange={setCtaEnabled}
          title="Closed to arrival"
          help="Prevent check-in on matching dates."
        >
          <ToggleValue value={cta} onChange={setCta} onLabel="Closed" offLabel="Open" />
        </FieldRow>

        <FieldRow
          checked={ctdEnabled}
          onCheckedChange={setCtdEnabled}
          title="Closed to departure"
          help="Prevent check-out on matching dates."
        >
          <ToggleValue value={ctd} onChange={setCtd} onLabel="Closed" offLabel="Open" />
        </FieldRow>

        <Divider style={{ margin: "18px 0 12px" }} />

        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", marginBottom: 10 }}>
          <div>
            <div style={{ fontWeight: 900, color: "#172033" }}>Booking Engine rate overrides</div>
            <div style={{ color: "#7b8497", fontSize: 12, marginTop: 3, lineHeight: 1.6 }}>
              Apply plan-specific selling rates to every matching date. OTA and PMS rates remain independent.
            </div>
          </div>
          <Tag color="blue">BE only</Tag>
        </div>

        {(rateOptions || []).length ? (
          <div style={{ display: "grid", gap: 10 }}>
            {(rateOptions || []).map((rate) => {
              const key = String(Number(rate.rate_id));
              const edit = rateEdits[key] || { enabled: false, guest_wise_rates: {} };
              const masterGuests = rate.guest_wise_rates || {};
              return (
                <div key={key} style={{ border: "1px solid #e7ebf2", borderRadius: 12, padding: 12, background: edit.enabled ? "#fbfdff" : "#fff" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                    <Checkbox
                      checked={!!edit.enabled}
                      onChange={(e) => patchRateEdit(rate.rate_id, { enabled: e.target.checked })}
                    >
                      <span style={{ fontWeight: 900, color: "#172033" }}>{rate.display_label || rate.plan_code || `Rate ${rate.rate_id}`}</span>
                    </Checkbox>
                    <Tag>{rate.plan_code || "BE"}</Tag>
                  </div>
                  <div style={{ marginLeft: 24, marginTop: 4, color: "#8b94a7", fontSize: 11 }}>
                    Blank values keep the existing date/master value.
                  </div>

                  <div style={{ marginTop: 10, marginLeft: 24, opacity: edit.enabled ? 1 : 0.45, pointerEvents: edit.enabled ? "auto" : "none" }}>
                    {rate.is_guest_wise_rate ? (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(125px,1fr))", gap: 8 }}>
                        {Object.keys(masterGuests).sort((a,b) => Number(a)-Number(b)).map((guestCount) => (
                          <div key={guestCount}>
                            <div style={{ color: "#7b8497", fontSize: 11, fontWeight: 700, marginBottom: 5 }}>
                              {guestCount} guest{Number(guestCount) === 1 ? "" : "s"}
                            </div>
                            <InputNumber
                              min={0}
                              value={edit.guest_wise_rates?.[guestCount] ?? null}
                              placeholder={`${currencySymbol(currencyCode)}${Number(masterGuests[guestCount] || 0).toLocaleString()}`}
                              onChange={(value) => patchRateEdit(rate.rate_id, {
                                guest_wise_rates: { ...(edit.guest_wise_rates || {}), [guestCount]: value },
                              })}
                              style={{ width: "100%" }}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <div>
                          <div style={{ color: "#7b8497", fontSize: 11, fontWeight: 700, marginBottom: 5 }}>Single</div>
                          <InputNumber
                            min={0}
                            value={edit.occ1_rate ?? null}
                            placeholder={`${currencySymbol(currencyCode)}${Number(rate.occ1_rate || 0).toLocaleString()}`}
                            onChange={(value) => patchRateEdit(rate.rate_id, { occ1_rate: value })}
                            style={{ width: "100%" }}
                          />
                        </div>
                        <div>
                          <div style={{ color: "#7b8497", fontSize: 11, fontWeight: 700, marginBottom: 5 }}>Double</div>
                          <InputNumber
                            min={0}
                            value={edit.occ2_rate ?? null}
                            placeholder={`${currencySymbol(currencyCode)}${Number(rate.occ2_rate || 0).toLocaleString()}`}
                            onChange={(value) => patchRateEdit(rate.rate_id, { occ2_rate: value })}
                            style={{ width: "100%" }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <FieldRow
            checked={legacyRateEnabled}
            onCheckedChange={setLegacyRateEnabled}
            title="Date base rate"
            help="No active BE rate options exist, so the legacy base override is used."
          >
            <div style={{ display: "flex", width: "100%" }}>
              <Input
                value={currencyCode}
                onChange={(e) => setCurrencyCode((e.target.value || currency || "INR").toUpperCase())}
                style={{ width: 68, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                maxLength={3}
              />
              <InputNumber
                min={0}
                value={legacyOverrideRate}
                onChange={setLegacyOverrideRate}
                placeholder={`${currencySymbol(currencyCode)} Rate`}
                style={{ width: "100%", marginLeft: -1 }}
              />
            </div>
          </FieldRow>
        )}

        {activeRateOverrides.length ? (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12, borderRadius: 10 }}
            message={`${activeRateOverrides.length} rate option${activeRateOverrides.length === 1 ? "" : "s"} will be staged`}
            description="These changes are not saved yet. You can still open any date after applying and fine-tune its rate before Save Changes."
          />
        ) : null}
      </div>
      </div>

      {exceedsPhysicalInventory ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16, borderRadius: 10 }}
          message="Availability is above physical room inventory"
          description={`${allotment} rooms will be staged while this room type is configured with ${roomType?.total_rooms || 0} physical rooms. The current backend allows this value; review your overbooking policy before saving.`}
        />
      ) : null}

      <div className="be-bulk-preview-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: "#7b8497", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".06em" }}>
              Preview
            </div>
            <div style={{ marginTop: 4, fontWeight: 900, color: "#172033" }}>
              {affectedDates.length} date{affectedDates.length === 1 ? "" : "s"} · {roomType?.name || "Room Type"}
            </div>
          </div>
          <Tag color={affectedDates.length ? "processing" : "default"}>
            Not saved yet
          </Tag>
        </div>
        <div style={{ marginTop: 8, color: "#7b8497", fontSize: 12, lineHeight: 1.7 }}>
          Changes will be applied to the visible calendar only. You can still open any individual date, adjust it, and then use the page-level <b>Save Changes</b> action once.
        </div>
      </div>

      <style>{`
        .be-bulk-premium-drawer .ant-drawer-content { background:#f7f9fc; }
        .be-bulk-premium-drawer .ant-drawer-header-title { align-items:flex-start; }
        .be-bulk-premium-drawer .ant-drawer-close {
          width:36px; height:36px; border-radius:10px; display:grid; place-items:center;
          color:#475569; background:#f8fafc; border:1px solid #e2e8f0; margin-inline-end:12px;
        }
        .be-bulk-premium-drawer .ant-drawer-close:hover { background:#eef4ff; color:#155eef; border-color:#c7d7fe; }
        .be-bulk-scope-card {
          background:linear-gradient(135deg,#eef5ff 0%,#ffffff 58%); border:1px solid #dfe8f5;
          border-radius:16px; padding:16px 17px; margin-bottom:14px; box-shadow:0 6px 22px rgba(15,23,42,.04);
        }
        .be-bulk-section-card {
          background:#fff; border:1px solid #e6ebf2; border-radius:16px; padding:16px 17px;
          box-shadow:0 7px 24px rgba(15,23,42,.035);
        }
        .be-bulk-preview-card {
          margin-top:14px; border:1px solid #dce7f5; background:linear-gradient(135deg,#f8fbff,#fff);
          border-radius:14px; padding:14px 15px; box-shadow:0 6px 18px rgba(15,23,42,.035);
        }
        .be-bulk-section-head { display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
        .be-bulk-section-icon { width:34px; height:34px; display:grid; place-items:center; border-radius:10px; background:#eef5ff; color:#155eef; flex:0 0 auto; }
        .be-bulk-quick-ranges { margin-left:auto; display:flex; gap:6px; flex-wrap:wrap; }
        .be-bulk-react-range { display:grid; grid-template-columns:minmax(0,1fr) 34px minmax(0,1fr); gap:10px; align-items:start; }
        .be-bulk-react-date-field { min-width:0; }
        .be-bulk-date-label { display:block; font-size:11px; font-weight:900; color:#475569; text-transform:uppercase; letter-spacing:.045em; margin-bottom:6px; }
        .be-bulk-react-date-wrap { position:relative; }
        .be-bulk-react-date-wrap .react-datepicker-wrapper, .be-bulk-react-date-wrap .react-datepicker__input-container { width:100%; }
        .be-bulk-react-date-icon { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:#64748b; z-index:2; pointer-events:none; }
        .be-bulk-react-date-input { width:100%; min-height:42px; border:1px solid #dfe5ee; border-radius:10px; padding:8px 12px 8px 38px; background:#fff; color:#172033; font-size:13px; font-weight:800; outline:none; transition:border-color .15s ease, box-shadow .15s ease; }
        .be-bulk-react-date-input:focus { border-color:#8bb7ff; box-shadow:0 0 0 3px rgba(21,94,239,.08); }
        .be-bulk-range-arrow { height:42px; margin-top:23px; display:grid; place-items:center; color:#94a3b8; }
        .be-bulk-date-help { margin-top:5px; color:#94a3b8; font-size:10.5px; line-height:1.4; }
        .be-bulk-react-popper { z-index:100100 !important; }
        .be-bulk-react-calendar { border:1px solid #dfe7f2 !important; box-shadow:0 18px 45px rgba(15,23,42,.16); border-radius:14px !important; overflow:hidden; font-family:inherit; }
        .be-bulk-react-calendar .react-datepicker__header { background:#f8fbff; border-bottom:1px solid #e6edf6; }
        .be-bulk-react-calendar .react-datepicker__day--selected, .be-bulk-react-calendar .react-datepicker__day--in-range, .be-bulk-react-calendar .react-datepicker__day--keyboard-selected { background:#1677ff; color:#fff; border-radius:8px; }
        .be-bulk-react-calendar .react-datepicker__day--disabled { color:#cbd5e1; }
        .be-bulk-days-wrap { margin-top:15px; padding-top:14px; border-top:1px solid #edf1f5; }
        .be-bulk-days-head { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:10px; flex-wrap:wrap; }
        .be-bulk-dow-grid { display:grid; grid-template-columns:repeat(7,minmax(0,1fr)); gap:7px; }
        .be-bulk-dow { border:1px solid #dfe5ee; border-radius:9px; background:#fff; color:#64748b; min-height:34px; font-size:12px; font-weight:800; transition:all .15s ease; }
        .be-bulk-dow:hover { border-color:#9abfff; color:#155eef; background:#f6f9ff; }
        .be-bulk-dow.is-active { color:#155eef; border-color:#9abfff; background:#eef5ff; box-shadow:inset 0 0 0 1px rgba(21,94,239,.08); }
        @media (max-width: 767px) {
          .be-bulk-quick-ranges { width:100%; margin-left:44px; }
          .be-bulk-react-range { grid-template-columns:1fr; gap:10px; }
          .be-bulk-range-arrow { display:none; }
          .be-bulk-dow-grid { grid-template-columns:repeat(4,minmax(0,1fr)); }
        }
      `}</style>
    </Drawer>
  );
}
